import { useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, X, Loader2, Bug, Lightbulb, Heart } from 'lucide-react';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';

// ── FeedbackButton ─────────────────────────────────────────────────────
//
// Floating bottom-right button visible on every logged-in page. Opens a
// right-side drawer with a compact form (subject / category / message).
// Submits via the existing /support/tickets endpoint under the platform
// scope so the ticket lands in the BrixLead super-admin inbox rather
// than the tenant's internal team queue.
//
// Rationale for reusing support tickets instead of adding a new table:
//   1. Same inbox to triage — one workflow, not two
//   2. Same status lifecycle (open / in-progress / resolved / closed)
//   3. Same email routing to support@brixlead.com
//   4. Same audit trail
// A dedicated "product feedback" surface can grow from this later if
// volume warrants; for launch, one funnel is enough.

const CATEGORIES = [
  { key: 'bug',     label: 'Bug',              icon: Bug,       hint: 'Something is broken or throws an error' },
  { key: 'feature', label: 'Feature request',  icon: Lightbulb, hint: 'You wish BrixLead could do X' },
  { key: 'general', label: 'General feedback', icon: Heart,     hint: 'Comments, praise, or anything else' },
] as const;

type Category = typeof CATEGORIES[number]['key'];

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<Category>('general');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCategory('general');
    setSubject('');
    setMessage('');
  };

  const submit = async () => {
    if (!subject.trim() || !message.trim()) {
      toast('error', 'Subject and message are required');
      return;
    }
    setSubmitting(true);
    try {
      // Product feedback goes to its own inbox (PlatformFeedback), kept
      // separate from support tickets. Notifies the super-admin bell + email.
      await api.post('/feedback/product', {
        category,
        subject: subject.trim(),
        message: message.trim(),
      });
      toast('success', 'Thanks — we\'ll reply within 48 hours');
      reset();
      setOpen(false);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to send feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const drawer = open && createPortal(
    <div className="fixed inset-0 z-[100] flex justify-end bg-black/30">
      <div className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 flex-shrink-0">
          <div>
            <h3 className="text-base font-bold text-slate-900">Send Feedback</h3>
            <p className="text-xs text-slate-500 mt-0.5">We read every message. Response within 48 hours.</p>
          </div>
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700 rounded p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Category — visual cards, not a dropdown, so the choice is
              deliberate. Bug reports auto-get high priority. */}
          <div>
            <label className="block text-xs font-normal text-slate-600 mb-2">Category *</label>
            <div className="grid grid-cols-3 gap-2">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const active = category === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setCategory(c.key)}
                    className={`flex flex-col items-center gap-1.5 rounded-lg border p-3 transition-colors ${
                      active
                        ? 'border-[#6366F1] bg-indigo-50/60'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${active ? 'text-[#6366F1]' : 'text-slate-400'}`} />
                    <span className={`text-xs font-medium ${active ? 'text-[#6366F1]' : 'text-slate-700'}`}>
                      {c.label}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              {CATEGORIES.find((c) => c.key === category)?.hint}
            </p>
          </div>

          <div>
            <label className="block text-xs font-normal text-slate-600 mb-1.5">Subject *</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Short summary of what happened / what you'd like"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40"
              maxLength={200}
            />
          </div>

          <div>
            <label className="block text-xs font-normal text-slate-600 mb-1.5">Message *</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              placeholder={
                category === 'bug'
                  ? 'What did you try to do? What happened? What did you expect?'
                  : category === 'feature'
                  ? 'What problem would this feature solve for you?'
                  : 'Tell us anything — what you like, what confuses you, what\'s missing.'
              }
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40 resize-none"
              maxLength={5000}
            />
            <p className="text-[10px] text-slate-400 mt-1">{message.length}/5000</p>
          </div>

          <p className="text-[11px] text-slate-400 leading-relaxed">
            This message is sent to the BrixLead support team along with your account email so we can respond. See our{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#6366F1] hover:underline">Privacy Policy</a>.
          </p>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-end gap-2 flex-shrink-0">
          <button
            onClick={() => setOpen(false)}
            className="text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !subject.trim() || !message.trim()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm font-semibold text-white shadow-sm"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Send Feedback
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );

  return (
    <>
      {/* Floating trigger button — bottom-right, brand indigo, drops
          shadow on the icon so it reads as elevated. Positioned to not
          overlap with other floating widgets (NpsWidget uses same
          quadrant; z-index + offset gives them separate lanes). */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        title="Send feedback"
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-1.5 rounded-full bg-[#6366F1] hover:bg-[#4F46E5] text-white shadow-lg hover:shadow-xl transition-all px-4 py-3 font-semibold text-sm"
      >
        <MessageCircle className="h-4 w-4" />
        <span className="hidden sm:inline">Feedback</span>
      </button>
      {drawer}
    </>
  );
}
